/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Blend, Check, Lock } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { sessionKeys } from '@/queries/session';
import { taskKeys } from '@/queries/tasks';

/**
 * `/c/:circleId` — somebody followed a link to a circle.
 *
 * **This is a page of its own, and that is what makes it allowed.** The active
 * circle is deliberately not in the board's URL: `web/src/types/session.ts` says
 * a link carrying it *"would silently switch the circle of whoever opened it"*,
 * and that is still true. Here nothing is silent — the person clicked a link
 * whose entire purpose is this circle, so switching is the thing they asked for.
 *
 * Three outcomes, and the middle one is the feature:
 *
 *  - **you can reach it** — switch, and go to the board
 *  - **you cannot** — the circle's name, how big it is, who runs it, and a box
 *    to say why you need in. Nothing about what is inside it
 *  - **it is not there** — a plain sentence that names nothing, because a link
 *    to a circle that does not exist must not become a way to discover one that
 *    does
 */
interface Summary {
  id: string;
  name: string;
  color: string;
  memberCount: number;
  access: 'member' | 'none';
  admins: string[];
  requestPending: boolean;
}

export function CircleLink() {
  const { circleId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, error, isLoading } = useQuery<Summary>({
    queryKey: ['circle-summary', circleId],
    queryFn: () => api.get<Summary>(`/api/circles/${circleId}/summary`),
    // A circle link is opened once and acted on. Retrying a 404 four times only
    // makes the "not here" message slower to arrive.
    retry: false,
  });

  /**
   * A member is switched and sent on.
   *
   * In an effect rather than during render, because it both mutates server state
   * and navigates — and it must happen exactly once, not on every re-render the
   * query causes.
   */
  const [switching, setSwitching] = useState(false);
  useEffect(() => {
    if (data?.access !== 'member' || switching) return;
    setSwitching(true);
    (async () => {
      try {
        await api.post('/api/circles/active', { circleId });
        await qc.invalidateQueries({ queryKey: sessionKeys.circles });
        await qc.invalidateQueries({ queryKey: taskKeys.all });
      } catch {
        // Landing on the board showing the wrong circle is a great deal better
        // than being stranded on a blank page. The switcher is right there.
      }
      navigate('/board', { replace: true });
    })();
  }, [data?.access, circleId, navigate, qc, switching]);

  if (isLoading || data?.access === 'member') return <Waiting />;

  if (error) {
    const missing = error instanceof ApiError && error.isMissing;
    return (
      <Shell>
        <p id="circle-link-missing" className="m-0 text-sm text-ink-muted">
          {missing
            ? 'That circle is not here. It may have been archived, or the link may be out of date.'
            : 'That link could not be opened just now. Try again in a moment.'}
        </p>
      </Shell>
    );
  }

  if (!data) return <Waiting />;
  return <RequestAccess circle={data} />;
}

function RequestAccess({ circle }: { circle: Summary }) {
  const qc = useQueryClient();
  const [message, setMessage] = useState('');

  const ask = useMutation({
    mutationFn: () => api.post(`/api/circles/${circle.id}/access-request`, { message: message.trim() }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['circle-summary', circle.id] }),
  });

  const sent = circle.requestPending || ask.isSuccess;

  return (
    <Shell>
      <div className="flex items-center gap-2.5">
        <span
          className="size-3 shrink-0 rounded-full"
          style={{ background: circle.color || 'var(--color-border-strong)' }}
          aria-hidden
        />
        <h1 id="circle-link-name" className="m-0 text-xl font-semibold tracking-[-0.01em] text-ink">
          {circle.name}
        </h1>
      </div>

      <p className="m-0 mt-1 text-2xs text-ink-faint">
        {circle.memberCount} {circle.memberCount === 1 ? 'person' : 'people'}
        {circle.admins.length > 0 && <> · administered by {circle.admins.join(', ')}</>}
      </p>

      {sent ? (
        // **Not a form any more.** Somebody who has asked cannot usefully ask
        // again, and leaving the box there invites them to keep pressing it.
        <div id="circle-link-sent" className="mt-5 flex items-start gap-2.5 rounded-xl border border-border bg-bg px-3.5 py-3">
          <Check className="mt-0.5 size-4 shrink-0 text-teal" aria-hidden />
          <p className="m-0 text-sm leading-relaxed text-ink-muted">
            Your request has been sent. {circle.admins.length > 0
              ? `${circle.admins[0]}${circle.admins.length > 1 ? ' and the other administrators' : ''} will see it in Qollab.`
              : 'An administrator will see it in Qollab.'}
            {' '}You will get an email either way.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-border bg-bg px-3.5 py-3">
            <Lock className="mt-0.5 size-4 shrink-0 text-ink-faint" aria-hidden />
            <p className="m-0 text-sm leading-relaxed text-ink-muted">
              You are not in this circle yet, so its board is not shown. Ask an
              administrator to add you.
            </p>
          </div>

          <label className="field-label mt-5 block" htmlFor="circle-link-why">
            Why do you need access?
          </label>
          {/* The reason is what turns a bare name in a queue into a decision
              somebody can actually make. Optional, because insisting on one
              would just produce the word "access" five hundred times. */}
          <textarea
            id="circle-link-why"
            rows={3}
            maxLength={500}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Covering for a colleague, joining the project, …"
            className="mt-1 w-full resize-y rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-action"
          />

          <button
            id="circle-link-request"
            type="button"
            className="btn btn-primary mt-3 w-full"
            disabled={ask.isPending}
            onClick={() => ask.mutate()}
          >
            {ask.isPending ? 'Sending…' : 'Request access'}
          </button>
          {ask.isError && (
            <p className="m-0 mt-2 text-2xs text-danger">
              That did not send. Try again in a moment.
            </p>
          )}
        </>
      )}

      <a href="/board" className="mt-5 inline-block text-2xs font-semibold text-ink-faint no-underline hover:text-ink-muted">
        Go to my board
      </a>
    </Shell>
  );
}

/**
 * The frame both states sit in.
 *
 * A centred card on the page background rather than inside the app shell: this
 * route has no board behind it, and rendering the topbar over a circle the
 * person cannot open would be showing them the switcher for somewhere else.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-4">
      <div className="w-[min(460px,94vw)] rounded-2xl border border-border bg-surface p-6 shadow-overlay">
        <span className="mb-4 grid size-9 place-items-center rounded-xl bg-action-bg text-action">
          <Blend className="size-[18px]" aria-hidden />
        </span>
        {children}
      </div>
    </div>
  );
}

function Waiting() {
  // Deliberately wordless. A member is here for a few hundred milliseconds on
  // the way to their board, and "Loading circle…" would flash and go.
  return <div className="grid min-h-dvh place-items-center bg-bg" aria-busy />;
}
